/**
 * Underwriting Table Configuration
 * 
 * Configuration for table view of underwriting feedback
 * Transforms card-based data into tabular format
 */

import { UNDERWRITING_CONFIG, getValueFromPath } from './underwritingFeedbackConfig';

/**
 * Category to color mapping (reusing existing palette)
 */
const CATEGORY_COLOR_MAP = {
  red: '#dc3545',    // High risk
  amber: '#ffc107',  // Medium risk
  green: '#28a745'   // Low risk
};

/**
 * Get table rows from new simplified structure
 * @param {Object} data - New structure with overall_decision and details[]
 * @returns {Array} Array of table row objects
 */
export const getTableRowsFromNewStructure = (data) => {
  if (!data?.details) return [];
  
  return data.details.map((item, index) => {
    // Normalize columns to ensure consistent structure across all rows
    let normalizedColumns = [];
    
    if (item.columns && item.columns.length > 0) {
      // Check if this row has any column with a list/array header
      const hasArrayHeader = item.columns.some(col => Array.isArray(col.header));
      
      if (hasArrayHeader) {
        // This row has combined columns - we need to expand it to match the standard structure
        // while marking which columns should be combined
        const sectionCol = item.columns.find(col => col.header === 'Section');
        const combinedCol = item.columns.find(col => Array.isArray(col.header));
        const feedbackNeededCol = item.columns.find(col => col.header === 'Feedback Needed');
        const overallCol = item.columns.find(col => col.header === 'Overall');
        
        // Get the list of headers that should be combined
        const combinedHeaders = Array.isArray(combinedCol?.header) ? combinedCol.header : [];
        
        // Build normalized columns in standard order: Section, Red, Amber, Green, Feedback Needed, Overall
        // Mark the combined column with isCombined flag and span info
        normalizedColumns = [
          sectionCol || { header: 'Section', content: [''], content_type: 'text', background_color: '#FFFFFF' },
          { 
            header: 'Red', 
            content: combinedCol ? combinedCol.content : [''], 
            content_type: combinedCol ? combinedCol.content_type : 'text',
            background_color: combinedCol ? combinedCol.background_color : '#fdecea',
            extra_info: combinedCol ? combinedCol.extra_info : '',
            isCombined: combinedHeaders.includes('Red'),
            combinedSpan: combinedHeaders.length,
            combinedHeaders: combinedHeaders
          },
          { 
            header: 'Amber', 
            content: combinedHeaders.includes('Amber') && combinedCol ? combinedCol.content : [''], 
            content_type: 'text',
            background_color: combinedHeaders.includes('Amber') && combinedCol ? combinedCol.background_color : '#fff4e5',
            isCombined: combinedHeaders.includes('Amber'),
            combinedSpan: combinedHeaders.length,
            combinedHeaders: combinedHeaders
          },
          { 
            header: 'Green', 
            content: combinedHeaders.includes('Green') && combinedCol ? combinedCol.content : [''], 
            content_type: 'text',
            background_color: combinedHeaders.includes('Green') && combinedCol ? combinedCol.background_color : '#e6f4ea',
            isCombined: combinedHeaders.includes('Green'),
            combinedSpan: combinedHeaders.length,
            combinedHeaders: combinedHeaders
          },
          feedbackNeededCol || { header: 'Feedback Needed', content: [''], content_type: 'text', background_color: '#FFF9C4' },
          overallCol || { header: 'Overall', content: [''], content_type: 'text', background_color: '#FFFFFF' }
        ];
      } else {
        // Standard row structure - use as-is
        normalizedColumns = item.columns;
      }
    }
    
    return {
      id: `section-${index}`,
      section: item.section,
      columns: normalizedColumns,
      isNewStructure: true
    };
  });
};

/**
 * Get table rows data from response
 * @param {Object} data - Full underwriting response data
 * @returns {Array} Array of table row objects
 */
export const getTableRowsData = (data) => {
  if (!data) return [];

  const rows = [];

  UNDERWRITING_CONFIG.sections.forEach(sectionConfig => {
    // Check if this is a tool-based section
    const isToolSection = sectionConfig.toolName && sectionConfig.requiredPath;
    let toolExecuted = true; // Default to true for non-tool sections
    
    if (isToolSection) {
      toolExecuted = getValueFromPath(data, sectionConfig.requiredPath) === true;
    }

    const sectionData = getValueFromPath(data, sectionConfig.dataPath);

    // Get document name - rename Overall Decision to Overall Summary for table view
    const document = sectionConfig.id === 'overall_risk' ? 'Overall Summary' : sectionConfig.title;

  
    const explanationConfig = sectionConfig.explanation;

    let comments = null;

    if (explanationConfig) {
      const sourceData =
        explanationConfig.source === 'root'
          ? data
          : sectionData;

      const rawData = getValueFromPath(sourceData, explanationConfig.dataPath);
      
      // Check if a custom formatter is defined
      if (explanationConfig.formatter && typeof explanationConfig.formatter === 'function') {
        // Apply custom formatter
        comments = explanationConfig.formatter(rawData);
      } else {
        // Use raw data as-is
        comments = rawData;
      }
    }

    // If tool was not executed, get rationale
    let toolRationale = null;
    if (isToolSection && !toolExecuted) {
      const toolEvaluation = getValueFromPath(
        data, 
        `tool_selector_module.raw_output.thought_trace.tool_evaluation.${sectionConfig.toolName}`
      );
      toolRationale = toolEvaluation || 'Tool was not required for this case.';
    }

    // Get severity (risk grade) - but not for tool sections
    let severity = null;
    if (!isToolSection) {
      if (sectionConfig.displayGrade && sectionConfig.displayGrade.dataPath) {
        severity = getValueFromPath(data, sectionConfig.displayGrade.dataPath);
      } else if (sectionConfig.mainGrade) {
        severity = getValueFromPath(sectionData, sectionConfig.mainGrade.dataPath);
      } else if (sectionConfig.fields && sectionConfig.fields.length > 0 && sectionConfig.fields[0].displayType === 'badge') {
        severity = getValueFromPath(sectionData, sectionConfig.fields[0].dataPath);
      }
    }

    // Get additional info for overall decision
    let decision = null;
    if (sectionConfig.id === 'overall_risk') {
      decision = getValueFromPath(data, 'final_decision.decision');
    }

    // Add all sections to table (even without feedback)
    rows.push({
      id: sectionConfig.id,
      document,
      comments: toolExecuted ? comments : toolRationale,
      severity,
      decision,
      feedbackConfig: sectionConfig.feedback || null, // Can be null for sections without feedback
      hasFeedback: !!sectionConfig.feedback, // Flag to indicate if feedback is available
      isToolSection,
      toolExecuted,
      isOverallDecision: sectionConfig.id === 'overall_risk'
    });
  });

  // Sort rows to place Overall Summary at the end
  rows.sort((a, b) => {
    if (a.id === 'overall_risk') return 1;
    if (b.id === 'overall_risk') return -1;
    return 0;
  });

  return rows;
};

/**
 * Parse comments into bullet points
 * @param {string|Array} text - Comments text or array
 * @returns {Array} Array of bullet point strings
 */
export const parseCommentsToBullets = (text) => {
  if (!text) return [];
  
  // If already an array, return as-is (filter empty items)
  if (Array.isArray(text)) {
    return text.filter(item => item && String(item).trim().length > 0);
  }
  
  // Handle non-string types (object, etc.)
  if (typeof text !== 'string') {
    if (typeof text === 'object') {
      return [];
    }
    // Convert to string as fallback
    text = String(text);
  }
  
  // Split by common delimiters
  const sentences = text
    .split(/[.!?]+/)
    .map(s => s.trim())
    .filter(s => s.length > 0);
  
  // Return all sentences (no limit)
  return sentences;
};

/**
 * Get severity color
 * @param {string} severity - Risk grade value
 * @returns {string} Color hex code
 */
export const getSeverityColor = (severity) => {
  return UNDERWRITING_CONFIG.riskColors[severity] || '#6c757d';
};
